/**
 * Change Password Settings Page
 *
 * Lets a signed-in user set a new password without leaving the app.
 * Uses supabase.auth.updateUser() — no email round-trip required.
 */

import { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../../lib/supabaseClient';

function passwordStrengthError(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  return null;
}

export default function ChangePassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const strengthError = passwordStrengthError(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) {
        setError(updateErr.message || 'Failed to update password. Try again.');
        return;
      }
      setSuccess(true);
      setPassword('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg">
      <PageHeader
        icon={<Lock className="h-4 w-4 text-consulting-slate-blue-600 dark:text-[var(--text-ter)]" />}
        iconBg="bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)]"
        title="Change Password"
        subtitle="Set a new password for your account"
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="new-password"
            className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1"
          >
            New password
          </label>
          <div className="flex gap-2">
            <input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              className="no-dark flex-1 px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 dark:focus:ring-[var(--active-tint)] focus:border-consulting-slate-blue-500 dark:focus:border-[var(--slate)] outline-none transition-all text-sm"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="p-2 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] dark:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirm-password"
            className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1"
          >
            Confirm new password
          </label>
          <input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat your password"
            autoComplete="new-password"
            className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 dark:focus:ring-[var(--active-tint)] focus:border-consulting-slate-blue-500 dark:focus:border-[var(--slate)] outline-none transition-all text-sm"
            disabled={loading}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/50 p-3">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]/50 p-3">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Password updated successfully.
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-consulting-slate-blue-600 text-white text-sm font-medium hover:bg-consulting-slate-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Update password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
